import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, switchMap, tap } from 'rxjs';

import { NotificationSettingsModel } from '../../models/notification-settings.model';
import { NotificationSettingsResource } from '../../services/resources/notification-settings.resource';

@Component({
    selector: 'sl-notification-settings',
    templateUrl: './notification-settings.component.html',
    styleUrls: ['./notification-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NotificationSettingsComponent implements OnInit {
    isLoading = true;
    isInvalid = false;

    settingsFormGroup = new FormGroup({
        id: new FormControl(''),
        emailNotificationsEnabled: new FormControl(false),
        useSchedule: new FormControl(false),
        emailSchedule: new FormControl('')
    });

    private saveRequestDebounceTimeMs = 300;
    private settingsChangedSub = new Subject<NotificationSettingsModel>();

    private readonly notificationSettingsResource = inject(NotificationSettingsResource);

    constructor() {
        // when the settings changed, save it via backend request (only if changbed, with high debounce time)
        this.settingsChangedSub
            .pipe(
                distinctUntilChanged(
                    (prev, curr) =>
                        prev.emailNotificationsEnabled === curr.emailNotificationsEnabled && prev.emailSchedule === curr.emailSchedule
                ),
                tap(() => (this.isLoading = true)),
                debounceTime(this.saveRequestDebounceTimeMs),
                switchMap(settings => this.notificationSettingsResource.update(settings))
            )
            .subscribe(() => {
                this.isLoading = false;
            });
    }

    ngOnInit(): void {
        this.settingsFormGroup.controls.useSchedule.disable();
        this.settingsFormGroup.controls.emailSchedule.disable();

        this.settingsFormGroup.controls.emailNotificationsEnabled.valueChanges.subscribe(enabled => {
            if (enabled) {
                this.settingsFormGroup.controls.useSchedule.enable();
                if (this.settingsFormGroup.value.useSchedule) {
                    this.settingsFormGroup.controls.emailSchedule.enable();
                }
            } else {
                this.settingsFormGroup.controls.useSchedule.disable();
                this.settingsFormGroup.controls.emailSchedule.disable();
                this.settingsFormGroup.patchValue({ useSchedule: false, emailSchedule: '' });
            }
        });

        this.settingsFormGroup.controls.useSchedule.valueChanges.subscribe(useSchedule => {
            if (useSchedule) {
                this.settingsFormGroup.controls.emailSchedule.enable();
            } else {
                this.settingsFormGroup.controls.emailSchedule.disable();
                this.settingsFormGroup.patchValue({ emailSchedule: '' });
            }
        });

        this.notificationSettingsResource.get().subscribe(response => {
            const settings = response.value ?? new NotificationSettingsModel();
            this.settingsFormGroup.patchValue({ ...settings, useSchedule: !!settings.emailSchedule });
            this.isLoading = false;

            // api request on form change
            this.settingsFormGroup.valueChanges.subscribe(() => {
                this.settingsFormGroup.markAllAsTouched();
                // don't trigger valueChanges here to prevent recursive calls
                this.settingsFormGroup.updateValueAndValidity({ emitEvent: false });
                if (this.settingsFormGroup.invalid) {
                    this.isInvalid = true;
                    return;
                }

                this.isInvalid = false;
                const newSettings = new NotificationSettingsModel(this.settingsFormGroup.getRawValue() as NotificationSettingsModel);
                this.settingsChangedSub.next(newSettings);
            });
        });
    }
}
